import type { ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { InlineDocsHint, SectionLabel } from '@reflex/ui/components/layout/Typography';
import { AnimatedCollapse } from '@reflex/ui/components/shared/AnimatedCollapse';
import { cn } from '@reflex/ui/lib/utils';

export interface CollapsibleConfigSectionProps {
  /** Stable id used to derive the toggle `data-testid` and content region id. */
  id: string;
  title: string;
  open: boolean;
  onToggle: () => void;
  /**
   * Optional docs slug (with optional `#anchor`) rendered as an
   * {@link InlineDocsHint} in the header row, next to the title. Kept as a
   * sibling of the toggle button (not inside it) so the interactive docs
   * trigger isn't nested in another button.
   */
  docsSlug?: string;
  /** Optional content pinned to the right of the header (e.g. a status badge). */
  trailing?: ReactNode;
  /** Override the toggle label size (defaults to the dense `text-3xs`). */
  labelClassName?: string;
  children: ReactNode;
}

/**
 * A lightweight, frameless collapsible section — a disclosure row (chevron, a
 * small uppercase section label, and an optional trailing badge) that floats on
 * the surrounding surface rather than sitting inside a bordered card, keeping
 * the content it groups visually primary.
 *
 * Shared by the launch-agent config section (`AgentConfigFormSection`) and the
 * blueprint create form so both compose their chip/field groups identically.
 */
export function CollapsibleConfigSection({
  id,
  title,
  open,
  onToggle,
  docsSlug,
  trailing,
  labelClassName,
  children,
}: CollapsibleConfigSectionProps) {
  return (
    <section>
      {/* The header is a flex row rather than a single button so the docs hint
          (an interactive trigger) can sit beside the toggle without being
          nested inside it. The toggle button still spans the row for a large
          click target. */}
      <div className="group -mx-1 flex items-center gap-1.5 rounded-md px-1 py-1 transition-colors hover:bg-muted/30">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={`${id}-content`}
          data-testid={`${id}-toggle`}
          className="flex flex-1 items-center gap-1.5 text-left"
        >
          <ChevronRight
            className={cn(
              'h-3 w-3 shrink-0 text-muted-foreground/50 transition-transform group-hover:text-muted-foreground',
              open && 'rotate-90',
            )}
            aria-hidden
          />
          <SectionLabel as="span" tone="strong" className={cn('text-3xs', labelClassName)}>
            {title}
          </SectionLabel>
          {trailing ? <span className="ml-auto flex items-center">{trailing}</span> : null}
        </button>
        {docsSlug ? <InlineDocsHint slug={docsSlug} className="shrink-0 opacity-70" /> : null}
      </div>
      <AnimatedCollapse open={open}>
        <div id={`${id}-content`} className="px-1 pt-2.5">
          {children}
        </div>
      </AnimatedCollapse>
    </section>
  );
}
